import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  MapPin, ChevronRight, Search, ArrowLeft,
  X, Building2, Tag, CalendarDays, GraduationCap, Briefcase, Globe
} from 'lucide-react';
import { OFFICES, SERVICES, PRODUCTS, COURSES, CATEGORIES } from '../data/database';
import { 
  LOCATION_COUNTRIES as COUNTRIES, 
  getCountryBySlug, 
  buildLocationUrl,
} from '../data/database';
import { CATEGORY_ICONS } from '../components/CategoryIcons';
import { OfficesSection, ServicesSection, ProductsSection, CoursesSection } from '../components/HomeSections';
import BottomNav from '../components/BottomNav';
import { useI18n } from '../i18n';

const LocationPage = ({ 
  countrySlug = 'italy', 
  regionSlug = null, 
  citySlug = null, 
  categorySlug = null,
  onNavigate, 
  onBack 
}) => {
  const { t, tc, language } = useI18n();
  const lang = language;
  
  // Resolve location data
  const country = useMemo(() => getCountryBySlug(countrySlug), [countrySlug]);
  const region = useMemo(() => {
    if (!regionSlug || !country) return null;
    return country.regions.find(r => Object.values(r.slugs).includes(regionSlug));
  }, [country, regionSlug]);
  const city = useMemo(() => {
    if (!citySlug || !region) return null;
    return region.cities.find(c => Object.values(c.slugs).includes(citySlug));
  }, [region, citySlug]);
  const category = useMemo(() => {
    if (!categorySlug) return null;
    return CATEGORIES.find(c => c.slug === categorySlug);
  }, [categorySlug]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categorySlug || 'all');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef(null);

  // Get translated category name
  const getCategoryName = (cat) => tc(cat?.slug) || cat?.name || '';

  // Build page title and description
  const pageTitle = useMemo(() => {
    const cityName = city?.names[lang] || city?.names.it;
    const catName = getCategoryName(category);
    if (city && category) return t('locations', 'bestInCity', { category: catName, city: cityName });
    if (city) return cityName;
    if (region) return region.names[lang] || region.names.it;
    if (country) return country.names[lang] || country.names.it;
    return t('locations', 'title');
  }, [country, region, city, category, lang, t, tc]);

  const pageSubtitle = useMemo(() => {
    const locationName = city?.names[lang] || city?.names.it || region?.names[lang] || region?.names.it || country?.names[lang] || country?.names.it;
    const catName = getCategoryName(category)?.toLowerCase();
    if (city && category) return t('locations', 'allCategoryInCity', { category: catName, city: locationName });
    if (city) return t('locations', 'discoverBusinessesIn', { location: locationName });
    if (region) return t('locations', 'exploreCitiesIn', { location: locationName });
    if (country) return t('locations', 'discoverAllRegions', { location: locationName });
    return '';
  }, [country, region, city, category, lang, t, tc]);

  // Breadcrumb
  const breadcrumbs = useMemo(() => {
    const crumbs = [];
    if (country) {
      crumbs.push({ 
        label: country.names[lang] || country.names.it, 
        slug: country.slugs[lang] || country.slugs.en,
        flag: country.flag,
        type: 'country'
      });
    }
    if (region) {
      crumbs.push({ 
        label: region.names[lang] || region.names.it, 
        slug: region.slugs[lang] || region.slugs.en,
        type: 'region'
      });
    }
    if (city) {
      crumbs.push({ 
        label: city.names[lang] || city.names.it, 
        slug: city.slugs[lang] || city.slugs.en,
        type: 'city'
      });
    }
    if (category) {
      crumbs.push({ 
        label: category.name, 
        slug: null,
        type: 'category'
      });
    }
    return crumbs;
  }, [country, region, city, category, lang]);

  // Get mock data filtered by location hierarchy
  const locationOffices = useMemo(() => {
    let result = OFFICES;
    // Simulate location filtering based on hierarchy depth
    const seed = (country?.id || '').length + (region?.id || '').length * 2 + (city?.id || '').length * 3;
    result = result.filter((_, i) => (i + seed) % 3 !== 0 || !city);
    
    // Filter by category if selected
    if (selectedCategory !== 'all') {
      result = result.filter(b => b.categorySlug === selectedCategory);
    }
    
    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b => 
        b.name.toLowerCase().includes(q) || 
        b.category?.toLowerCase().includes(q)
      );
    }
    
    return result;
  }, [country, region, city, selectedCategory, searchQuery]);

  const locationServices = useMemo(() => {
    const officeIds = locationOffices.map(b => b.id);
    let result = SERVICES.filter(s => officeIds.includes(s.officeId));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q));
    }
    return result;
  }, [locationOffices, searchQuery]);

  const locationProducts = useMemo(() => {
    const officeIds = locationOffices.map(b => b.id);
    let result = PRODUCTS.filter(p => officeIds.includes(p.officeId));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q));
    }
    return result;
  }, [locationOffices, searchQuery]);

  // Eventi - modulo non ancora implementato
  const locationEvents = [];

  const locationCourses = useMemo(() => {
    const officeIds = locationOffices.map(b => b.id);
    let result = COURSES.filter(c => officeIds.includes(c.officeId));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(q));
    }
    return result;
  }, [locationOffices, searchQuery]);

  // Stats
  const stats = {
    offices: locationOffices.length,
    services: locationServices.length,
    products: locationProducts.length,
    courses: locationCourses.length
  };

  // Popular categories for this location (using existing CategoryIcons)
  const popularCategories = useMemo(() => {
    const catCounts = {};
    locationOffices.forEach(b => {
      if (b.categorySlug) {
        catCounts[b.categorySlug] = (catCounts[b.categorySlug] || 0) + 1;
      }
    });
    
    return Object.entries(catCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([slug, count]) => {
        const cat = CATEGORIES.find(c => c.slug === slug);
        if (!cat) return null;
        // Get icon from CategoryIcons (existing component)
        const IconComponent = CATEGORY_ICONS[slug] || Building2;
        return { ...cat, count, IconComponent };
      })
      .filter(Boolean);
  }, [locationOffices]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search suggestions
  const searchSuggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const query = searchQuery.toLowerCase();
    const suggestions = [];
    
    locationOffices.filter(b => b.name.toLowerCase().includes(query)).slice(0, 3).forEach(b => {
      suggestions.push({ type: 'office', id: b.id, name: b.name, subtitle: b.category, icon: Building2 });
    });
    locationServices.filter(s => s.name.toLowerCase().includes(query)).slice(0, 3).forEach(s => {
      const biz = OFFICES.find(b => b.id === s.officeId);
      suggestions.push({ type: 'service', id: s.id, name: s.name, subtitle: `€${s.price} • ${biz?.name || ''}`, icon: Briefcase });
    });
    locationProducts.filter(p => p.name.toLowerCase().includes(query)).slice(0, 2).forEach(p => {
      suggestions.push({ type: 'product', id: p.id, name: p.name, subtitle: `€${p.price}`, icon: Tag });
    });
    
    return suggestions.slice(0, 8);
  }, [searchQuery, locationOffices, locationServices, locationProducts]);

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    setSearchQuery('');
    const routeMap = { 'office': 'office', 'service': 'service-detail', 'product': 'product-detail' };
    onNavigate(routeMap[suggestion.type] || suggestion.type, { id: suggestion.id });
  };

  const handleNavigate = (type, params) => {
    const routeMap = {
      'office': 'office',
      'service': 'service-detail',
      'product': 'product-detail',
      'event': 'event-detail',
      'course': 'course-detail'
    };
    onNavigate(routeMap[type] || type, params);
  };

  const handleCategoryClick = (catSlug) => {
    setSelectedCategory(catSlug);
  };

  const handleBreadcrumbClick = (crumb, index) => {
    if (crumb.type === 'country') {
      onNavigate('location', { country: crumb.slug });
    } else if (crumb.type === 'region') {
      onNavigate('location', { country: breadcrumbs[0].slug, region: crumb.slug });
    } else if (crumb.type === 'city') {
      onNavigate('location', { country: breadcrumbs[0].slug, region: breadcrumbs[1].slug, city: crumb.slug });
    }
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedCategory !== 'all' || searchQuery !== '';

  // Dynamic theme based on hierarchy level
  const theme = useMemo(() => {
    if (category) return { gradient: 'from-amber-600 via-orange-500 to-red-500', bg: 'bg-amber-500', bgLight: 'bg-amber-100', text: 'text-amber-600', hover: 'hover:bg-amber-600' };
    if (city) return { gradient: 'from-rose-600 via-pink-500 to-orange-500', bg: 'bg-rose-500', bgLight: 'bg-rose-100', text: 'text-rose-600', hover: 'hover:bg-rose-600' };
    if (region) return { gradient: 'from-violet-600 via-purple-500 to-indigo-500', bg: 'bg-violet-500', bgLight: 'bg-violet-100', text: 'text-violet-600', hover: 'hover:bg-violet-600' };
    return { gradient: 'from-emerald-600 via-teal-500 to-cyan-500', bg: 'bg-emerald-500', bgLight: 'bg-emerald-100', text: 'text-emerald-600', hover: 'hover:bg-emerald-600' };
  }, [category, city, region]);

  // Location name for search placeholder
  const locationName = city?.names[lang] || city?.names.it || region?.names[lang] || region?.names.it || country?.names[lang] || country?.names.it || '';

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-0">
      {/* Hero Section */}
      <div className={`relative bg-gradient-to-br ${theme.gradient} overflow-hidden`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f9fafb"/>
          </svg>
        </div>

        <div className="relative max-w-screen-2xl mx-auto px-6 pt-6 pb-24 lg:pt-10 lg:pb-32">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-6">
            <button onClick={onBack || (() => window.history.back())} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">{t('common', 'back')}</span>
            </button>
            <div className="flex items-center gap-2 text-white/80">
              <Globe className="w-4 h-4" />
              <span className="text-xs sm:text-sm">{t('locations', 'skipresLocations')}</span>
            </div>
          </div>

          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-white/70 text-sm mb-6 overflow-x-auto pb-2">
            <button onClick={() => onNavigate('explore', {})} className="hover:text-white flex items-center gap-1 flex-shrink-0">
              <MapPin className="w-4 h-4" />
              {t('common', 'home')}
            </button>
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={i}>
                <ChevronRight className="w-4 h-4 flex-shrink-0" />
                {crumb.slug ? (
                  <button 
                    onClick={() => handleBreadcrumbClick(crumb, i)}
                    className="hover:text-white whitespace-nowrap flex items-center gap-1"
                  >
                    {crumb.flag && <span>{crumb.flag}</span>}
                    {crumb.label}
                  </button>
                ) : (
                  <span className="text-white font-medium whitespace-nowrap">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Hero content */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="w-20 h-20 lg:w-24 lg:h-24 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-6">
              {category ? (
                (() => {
                  const IconComp = CATEGORY_ICONS[category.slug] || MapPin;
                  return <IconComp className="w-10 h-10 lg:w-12 lg:h-12 text-white" />;
                })()
              ) : (
                <MapPin className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
              )}
            </div>
            <h1 className="text-3xl lg:text-5xl font-bold text-white mb-3">{pageTitle}</h1>
            <p className="text-white/80 text-base lg:text-lg max-w-2xl mx-auto mb-8">{pageSubtitle}</p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-3 lg:gap-6 mb-10">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2.5">
                <span className="block text-xl lg:text-2xl font-bold text-white">{stats.offices}</span>
                <span className="text-xs text-white/80">{t('stats', 'offices')}</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2.5">
                <span className="block text-xl lg:text-2xl font-bold text-white">{stats.services}</span>
                <span className="text-xs text-white/80">{t('stats', 'services')}</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2.5">
                <span className="block text-xl lg:text-2xl font-bold text-white">{stats.products}</span>
                <span className="text-xs text-white/80">{t('stats', 'products')}</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2.5">
                <span className="block text-xl lg:text-2xl font-bold text-white">{stats.events}</span>
                <span className="text-xs text-white/80">{t('stats', 'events')}</span>
              </div>
            </div>

            {/* Search Bar - FIXED: button always visible */}
            <div className="relative z-[200] max-w-3xl mx-auto" ref={searchRef}>
              <div className={`flex items-center bg-white rounded-full shadow-2xl transition-all ${searchFocused ? 'ring-4 ring-white/30' : ''}`}>
                <div className="flex-1 relative">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => { setSearchFocused(true); setShowSuggestions(true); }}
                    placeholder={t('locations', 'searchIn', { location: locationName })}
                    className="w-full pl-14 pr-4 py-4 lg:py-5 text-gray-900 text-base lg:text-lg placeholder-gray-400 outline-none rounded-l-full"
                    data-testid="location-search-input"
                  />
                </div>
                <button className={`flex items-center gap-2 ${theme.bg} ${theme.hover} text-white px-6 py-4 lg:py-5 rounded-r-full transition-colors font-medium`}>
                  <Search className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('common', 'search')}</span>
                </button>
              </div>

              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[200]">
                  <div className="p-2">
                    {searchSuggestions.map((suggestion) => (
                      <button
                        key={`${suggestion.type}-${suggestion.id}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors text-left"
                      >
                        <div className={`w-10 h-10 ${theme.bgLight} ${theme.text} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <suggestion.icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{suggestion.name}</p>
                          <p className="text-sm text-gray-500 truncate">{suggestion.subtitle}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-6 -mt-6 relative">
        
        {/* Sub-locations (Regions or Cities) - only show if not at category level */}
        {!city && !category && (
          <div className="bg-white rounded-2xl shadow-sm p-4 lg:p-6 mb-8">
            <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-4">
              {region ? t('locations', 'cities') : t('locations', 'regions')}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {(region ? region.cities : country?.regions || []).slice(0, 12).map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    if (region) {
                      onNavigate('location', { 
                        country: country.slugs[lang] || country.slugs.en,
                        region: region.slugs[lang] || region.slugs.en,
                        city: item.slugs[lang] || item.slugs.en
                      });
                    } else {
                      onNavigate('location', { 
                        country: country.slugs[lang] || country.slugs.en,
                        region: item.slugs[lang] || item.slugs.en
                      });
                    }
                  }}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100"
                >
                  <div className={`w-12 h-12 ${theme.bgLight} rounded-xl flex items-center justify-center`}>
                    <MapPin className={`w-6 h-6 ${theme.text}`} />
                  </div>
                  <span className="text-sm font-medium text-gray-700 text-center">
                    {item.names[lang] || item.names.it}
                  </span>
                  {item.isCapital && (
                    <span className="text-xs text-gray-400">{t('locations', 'capital')}</span>
                  )}
                </button>
              ))}
            </div>
            {(region ? region.cities : country?.regions || []).length > 12 && (
              <button className={`mt-4 ${theme.text} font-medium text-sm hover:underline`}>
                {t('common', 'viewAll')} →
              </button>
            )}
          </div>
        )}

        {/* Categories - using existing CategoryIcons */}
        <div className="bg-white rounded-2xl shadow-sm p-4 lg:p-6 mb-8 relative z-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg lg:text-xl font-bold text-gray-900">
              {city ? t('locations', 'popularCategories') : t('locations', 'exploreByCategory')}
            </h2>
            {hasActiveFilters && (
              <button onClick={clearFilters} className={`text-sm ${theme.text} hover:opacity-80 font-medium flex items-center gap-1`}>
                <X className="w-4 h-4" /> {t('common', 'removeFilters')}
              </button>
            )}
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {/* "All" button */}
            <button
              onClick={() => handleCategoryClick('all')}
              className={`flex flex-col items-center gap-2 p-3 lg:p-4 rounded-xl transition-all flex-shrink-0 min-w-[80px] lg:min-w-[100px] ${
                selectedCategory === 'all' 
                  ? `${theme.bg} text-white shadow-lg scale-105` 
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl flex items-center justify-center ${
                selectedCategory === 'all' ? 'bg-white/20' : 'bg-gray-100'
              }`}>
                <Globe className={`w-6 h-6 lg:w-7 lg:h-7 ${selectedCategory === 'all' ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <span className="text-xs lg:text-sm font-medium text-center">{t('common', 'all')}</span>
            </button>
            
            {/* Category buttons using existing CategoryIcons */}
            {popularCategories.map(cat => {
              const IconComponent = cat.IconComponent;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`flex flex-col items-center gap-2 p-3 lg:p-4 rounded-xl transition-all flex-shrink-0 min-w-[80px] lg:min-w-[100px] ${
                    selectedCategory === cat.slug 
                      ? `${theme.bg} text-white shadow-lg scale-105` 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl flex items-center justify-center ${
                    selectedCategory === cat.slug ? 'bg-white/20' : `bg-${cat.color}-100`
                  }`}>
                    <IconComponent className={`w-6 h-6 lg:w-7 lg:h-7 ${selectedCategory === cat.slug ? 'text-white' : `text-${cat.color}-600`}`} />
                  </div>
                  <span className="text-xs lg:text-sm font-medium text-center">{tc(cat.slug)}</span>
                  <span className={`text-xs ${selectedCategory === cat.slug ? 'text-white/70' : 'text-gray-400'}`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500">{t('common', 'activeFilters')}:</span>
            {selectedCategory !== 'all' && (
              <span className={`${theme.bgLight} ${theme.text} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1`}>
                {tc(selectedCategory)}
                <button onClick={() => setSelectedCategory('all')}><X className="w-3 h-3" /></button>
              </span>
            )}
            {searchQuery && (
              <span className={`${theme.bgLight} ${theme.text} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1`}>
                &quot;{searchQuery}&quot;
                <button onClick={() => setSearchQuery('')}><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>
        )}

        {/* Content Sections */}
        <OfficesSection 
          offices={locationOffices}
          title={city ? t('sections', 'officesIn', { location: city.names[lang] || city.names.it }) : t('sections', 'offices')}
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        <ServicesSection 
          services={locationServices.slice(0, 12)}
          title={t('sections', 'availableServices')}
          subtitle=""
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        <ProductsSection 
          products={locationProducts.slice(0, 12)}
          title={t('sections', 'productsForSale')}
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        {/* EventsSection rimosso - modulo eventi non ancora implementato */}

        <CoursesSection 
          courses={locationCourses.slice(0, 8)}
          title={t('sections', 'coursesAndTraining')}
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        {/* No results */}
        {hasActiveFilters && locationOffices.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center mb-8">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('common', 'noResultsFound')}</h3>
            <p className="text-gray-500 mb-4">{t('common', 'tryModifyFilters')}</p>
            <button onClick={clearFilters} className={`${theme.bg} ${theme.hover} text-white px-6 py-2 rounded-full font-medium transition-colors`}>
              {t('common', 'removeFilters')}
            </button>
          </div>
        )}

        {/* SEO Info Banner */}
        <div className={`relative overflow-hidden rounded-2xl mb-8 bg-gradient-to-r ${theme.gradient}`}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          </div>
          <div className="relative p-6 lg:p-10 text-white">
            <h3 className="text-xl lg:text-2xl font-bold mb-4">
              {category && city
                ? t('locations', 'findBestServicesIn', { location: `${tc(category.slug).toLowerCase()} ${city.names[lang] || city.names.it}` })
                : city 
                  ? t('locations', 'findBestServicesIn', { location: city.names[lang] || city.names.it })
                  : region
                    ? t('locations', 'exploreLocation', { location: region.names[lang] || region.names.it })
                    : t('locations', 'discoverLocation', { location: country?.names[lang] || country?.names.it || 'Italia' })
              }
            </h3>
            <p className="text-white/80 mb-6 max-w-2xl">
              {t('misc', 'platform')}
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-white/20 px-4 py-2 rounded-full text-sm">✓ {t('misc', 'easyBooking')}</span>
              <span className="bg-white/20 px-4 py-2 rounded-full text-sm">✓ {t('misc', 'verifiedReviews')}</span>
              <span className="bg-white/20 px-4 py-2 rounded-full text-sm">✓ {t('misc', 'securePayments')}</span>
            </div>
          </div>
        </div>

        {/* Skipres Branding */}
        <div className="text-center py-6 border-t border-gray-200 mb-20 lg:mb-0">
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-gray-700">Skipres</span> • {t('misc', 'platform')}
          </p>
        </div>
      </div>

      {/* Bottom Nav - Mobile only */}
      <div className="lg:hidden">
        <BottomNav 
          activeTab="explore" 
          setActiveTab={(tabId) => {
            const tabRoutes = {
              'explore': () => onNavigate('explore', {}),
              'calendar': () => onNavigate('calendar', {}),
              'orders': () => onNavigate('orders', {}),
              'profile': () => onNavigate('menu', {})
            };
            if (tabRoutes[tabId]) tabRoutes[tabId]();
          }}
          stayOnPageAfterAuth={false}
        />
      </div>
    </div>
  );
};

export default LocationPage;
